import type { components, operations } from '../../generated/api-schema.ts'
import { emitAdminSessionExpired } from '../../modules/admin/admin-session-events.ts'

const apiBaseUrl =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '/api/v1'

export type Moment = components['schemas']['Moment']
export type Edition = components['schemas']['Edition']
export type PublicAsset = NonNullable<components['schemas']['PublicAsset']>
export type Quote = components['schemas']['Quote']
export type ExpectedPricing = components['schemas']['ExpectedPricing']
export type Problem = components['schemas']['Problem']
export type EditionDetail =
  operations['getEdition']['responses'][200]['content']['application/json']
export type CreatedOrder =
  operations['createOrder']['responses'][201]['content']['application/json']
export type Order = operations['getOrder']['responses'][200]['content']['application/json']
export type Account =
  operations['getCurrentAccount']['responses'][200]['content']['application/json']
export type AdminSession = components['schemas']['AdminSession']
export type CreateAdminSession =
  operations['createAdminSession']['requestBody']['content']['application/json']
export type EntitledEdition =
  operations['listMyEditions']['responses'][200]['content']['application/json']['data'][number]
export type EditionReader =
  operations['getEditionReader']['responses'][200]['content']['application/json']
export type EditionAudio =
  operations['getEditionAudio']['responses'][200]['content']['application/json']
export type EditionVideo =
  operations['getEditionVideo']['responses'][200]['content']['application/json']
export type EditionInteractive =
  operations['getEditionInteractive']['responses'][200]['content']['application/json']
export type AdminCatalog =
  operations['listAdminCatalog']['responses'][200]['content']['application/json']
export type AdminMoment = AdminCatalog['moments'][number]
export type AdminEdition = AdminCatalog['editions'][number]
export type CreateAdminMoment =
  operations['createAdminMoment']['requestBody']['content']['application/json']
export type UpdateAdminMoment =
  operations['updateAdminMoment']['requestBody']['content']['application/json']
export type CreateAdminEdition =
  operations['createAdminEdition']['requestBody']['content']['application/json']
export type UpdateAdminEdition =
  operations['updateAdminEdition']['requestBody']['content']['application/json']
export type AdminAsset = components['schemas']['AdminAsset']
export type AdminAssetPage =
  operations['listAdminAssets']['responses'][200]['content']['application/json']
export type CreateAdminAsset =
  operations['createAdminAsset']['requestBody']['content']['application/json']
export type AdminExperience = components['schemas']['AdminExperience']
export type AdminExperiencePage =
  operations['listAdminExperiences']['responses'][200]['content']['application/json']
export type CreateAdminExperience =
  operations['createAdminExperience']['requestBody']['content']['application/json']
export type UpdateAdminExperience =
  operations['updateAdminExperience']['requestBody']['content']['application/json']
export type AdminOffer =
  operations['listAdminOffers']['responses'][200]['content']['application/json']['data'][number]
export type CreateAdminOffer =
  operations['createAdminOffer']['requestBody']['content']['application/json']
export type UpdateAdminOffer =
  operations['updateAdminOffer']['requestBody']['content']['application/json']
export type AdminCoupon =
  operations['listAdminCoupons']['responses'][200]['content']['application/json']['data'][number]
export type CreateAdminCoupon =
  operations['createAdminCoupon']['requestBody']['content']['application/json']
export type UpdateAdminCoupon =
  operations['updateAdminCoupon']['requestBody']['content']['application/json']
export type SiteSettings = components['schemas']['PublicSiteSetting']
export type PublicSiteSettingsView =
  operations['getPublicSiteSettings']['responses'][200]['content']['application/json']
export type AdminSiteSettingsView =
  operations['getAdminSiteSettings']['responses'][200]['content']['application/json']
export type AdminNewsletterPage =
  operations['listAdminNewsletterSubscriptions']['responses'][200]['content']['application/json']
export type AdminNewsletterSubscription = AdminNewsletterPage['data'][number]
export type AdminOperationsSummary =
  operations['getAdminOperationsSummary']['responses'][200]['content']['application/json']
export type AdminCustomerPage =
  operations['listAdminCustomers']['responses'][200]['content']['application/json']
export type AdminCustomer = AdminCustomerPage['data'][number]
export type AdminOrderPage =
  operations['listAdminOrders']['responses'][200]['content']['application/json']
export type AdminOrder = AdminOrderPage['data'][number]
export type AdminOrderDetail =
  operations['getAdminOrder']['responses'][200]['content']['application/json']
export type AdminPaymentPage =
  operations['listAdminPayments']['responses'][200]['content']['application/json']
export type AdminPayment = AdminPaymentPage['data'][number]
export type AdminEntitlementPage =
  operations['listAdminEntitlements']['responses'][200]['content']['application/json']
export type AdminEntitlement = AdminEntitlementPage['data'][number]
export type AdminAuditLogPage =
  operations['listAdminAuditLogs']['responses'][200]['content']['application/json']
export type AdminAuditLog = AdminAuditLogPage['data'][number]
export type GrantAdminEntitlement =
  operations['grantAdminEntitlement']['requestBody']['content']['application/json']
export type RevokeAdminEntitlement =
  operations['revokeAdminEntitlement']['requestBody']['content']['application/json']
export type AdminMigrationRun = components['schemas']['AdminMigrationRun']
export type AdminMigrationRunDetail = components['schemas']['AdminMigrationRunDetail']
export type AdminMigrationItem = components['schemas']['AdminMigrationItem']
export type AdminMigrationRunPage =
  operations['listAdminMigrationRuns']['responses'][200]['content']['application/json']
export type AdminMigrationItemPage =
  operations['listAdminMigrationItems']['responses'][200]['content']['application/json']
export type AdminMigrationManifest =
  operations['registerAdminMigrationManifest']['requestBody']['content']['application/json']['manifest']
export type AdminMigrationRunStatus = NonNullable<
  operations['listAdminMigrationRuns']['parameters']['query']
>['status']
export type AdminMigrationItemKind = NonNullable<
  operations['listAdminMigrationItems']['parameters']['query']
>['kind']
export type AdminMigrationItemStatus = NonNullable<
  operations['listAdminMigrationItems']['parameters']['query']
>['status']
export type AdminSystemHealth =
  operations['getAdminSystemHealth']['responses'][200]['content']['application/json']
export type AdminTechnicalReports =
  operations['getAdminTechnicalReports']['responses'][200]['content']['application/json']
export type AdminOutboxEventPage =
  operations['listAdminOutboxEvents']['responses'][200]['content']['application/json']
export type AdminOutboxEvent = AdminOutboxEventPage['data'][number]
export type AdminOutboxEventStatus = NonNullable<
  NonNullable<operations['listAdminOutboxEvents']['parameters']['query']>['status']
>
export type CreateAdminOutboxRedelivery =
  operations['createAdminOutboxRedelivery']['requestBody']['content']['application/json']
export type AdminOutboxRedelivery =
  operations['createAdminOutboxRedelivery']['responses'][200]['content']['application/json']
export type CreateAdminPaymentReconciliation =
  operations['createAdminPaymentReconciliation']['requestBody']['content']['application/json']
export type AdminPaymentReconciliation =
  operations['createAdminPaymentReconciliation']['responses'][200]['content']['application/json']

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem: Problem | null,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  const method = init?.method?.toUpperCase() ?? 'GET'
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (typeof init?.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (path.startsWith('/admin') && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    headers.set('X-Admin-Request', '1')
  }
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })
  if (!response.ok) {
    const contentType = response.headers.get('content-type') ?? ''
    const parsed = contentType.includes('json') ? ((await response.json()) as Problem) : null
    if (response.status === 401 && path.startsWith('/admin/') && path !== '/admin/auth/session') {
      emitAdminSessionExpired()
    }
    throw new ApiError(
      parsed?.detail ?? 'Não foi possível concluir a solicitação.',
      response.status,
      parsed,
    )
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

function queryString(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) params.set(key, String(value))
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

export const api = {
  listMoments: (cursor?: string) =>
    request<{ data: Moment[]; page: { nextCursor: string | null; hasMore: boolean } }>(
      `/moments${queryString({ cursor, limit: 50 })}`,
    ),
  getMoment: (slug: string) => request<Moment>(`/moments/${encodeURIComponent(slug)}`),
  listEditions: (momentSlug: string) =>
    request<{ data: Edition[]; page: { nextCursor: string | null; hasMore: boolean } }>(
      `/moments/${encodeURIComponent(momentSlug)}/editions?limit=100`,
    ),
  getEdition: (momentSlug: string, editionSlug: string) =>
    request<EditionDetail>(
      `/moments/${encodeURIComponent(momentSlug)}/editions/${encodeURIComponent(editionSlug)}`,
    ),
  getPublicSiteSettings: () => request<PublicSiteSettingsView>('/site-settings'),
  quote: (editionIds: readonly string[], couponCode?: string) =>
    request<Quote>('/checkout/quote', {
      method: 'POST',
      body: JSON.stringify({ editionIds, ...(couponCode ? { couponCode } : {}) }),
    }),
  createOrder: (input: {
    buyerName: string
    buyerEmail: string
    editionIds: readonly string[]
    couponCode?: string
    expectedPricing: ExpectedPricing
    idempotencyKey: string
  }) =>
    request<CreatedOrder>('/orders', {
      method: 'POST',
      headers: { 'Idempotency-Key': input.idempotencyKey },
      body: JSON.stringify({
        buyerName: input.buyerName,
        buyerEmail: input.buyerEmail,
        editionIds: input.editionIds,
        ...(input.couponCode ? { couponCode: input.couponCode } : {}),
        expectedPricing: input.expectedPricing,
      }),
    }),
  getOrder: (id: string, accessToken: string) =>
    request<Order>(`/orders/${encodeURIComponent(id)}${queryString({ accessToken })}`),
  requestMagicLink: (email: string) =>
    request<{ accepted: true }>('/auth/magic-links', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyMagicLink: (token: string) =>
    request<{ account: Account }>('/auth/magic-links/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  getMe: () => request<Account>('/me'),
  createAdminSession: (input: CreateAdminSession) =>
    request<AdminSession>('/admin/auth/session', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getAdminSession: () => request<AdminSession>('/admin/auth/session'),
  deleteAdminSession: () => request<void>('/admin/auth/session', { method: 'DELETE' }),
  listMyEditions: () =>
    request<operations['listMyEditions']['responses'][200]['content']['application/json']>(
      '/me/editions',
    ),
  getEditionReader: (editionId: string) =>
    request<EditionReader>(`/me/editions/${encodeURIComponent(editionId)}/reader`),
  getEditionAudio: (editionId: string) =>
    request<EditionAudio>(`/me/editions/${encodeURIComponent(editionId)}/audio`),
  getEditionVideo: (editionId: string) =>
    request<EditionVideo>(`/me/editions/${encodeURIComponent(editionId)}/video`),
  getEditionInteractive: (editionId: string) =>
    request<EditionInteractive>(`/me/editions/${encodeURIComponent(editionId)}/interactive`),
  editionDownloadUrl: (editionId: string) =>
    `${apiBaseUrl}/me/editions/${encodeURIComponent(editionId)}/download`,
  publicAssetContentUrl: (assetId: string) =>
    `${apiBaseUrl}/assets/${encodeURIComponent(assetId)}/content`,
  listAdminCatalog: () => request<AdminCatalog>('/admin/catalog'),
  createAdminMoment: (input: CreateAdminMoment) =>
    request<AdminMoment>('/admin/moments', { method: 'POST', body: JSON.stringify(input) }),
  updateAdminMoment: (id: string, input: UpdateAdminMoment) =>
    request<AdminMoment>(`/admin/moments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archiveAdminMoment: (id: string) =>
    request<AdminMoment>(`/admin/moments/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  createAdminEdition: (input: CreateAdminEdition) =>
    request<AdminEdition>('/admin/editions', { method: 'POST', body: JSON.stringify(input) }),
  updateAdminEdition: (id: string, input: UpdateAdminEdition) =>
    request<AdminEdition>(`/admin/editions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archiveAdminEdition: (id: string) =>
    request<AdminEdition>(`/admin/editions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  listAdminAssets: (
    input: {
      cursor?: string
      q?: string
      purpose?: AdminAsset['purpose']
      status?: AdminAsset['status']
      visibility?: AdminAsset['visibility']
    } = {},
  ) =>
    request<AdminAssetPage>(
      `/admin/assets${queryString({
        cursor: input.cursor,
        q: input.q,
        purpose: input.purpose,
        status: input.status,
        visibility: input.visibility,
        limit: 100,
      })}`,
    ),
  adminAssetContentUrl: (assetId: string) =>
    `${apiBaseUrl}/admin/assets/${encodeURIComponent(assetId)}/content`,
  createAdminAsset: (input: CreateAdminAsset) =>
    request<AdminAsset>('/admin/assets', { method: 'POST', body: JSON.stringify(input) }),
  uploadAdminAssetContent: (input: {
    id: string
    file: File
    mediaType: CreateAdminAsset['mediaType']
    sha256: string
  }) =>
    request<AdminAsset>(`/admin/assets/${encodeURIComponent(input.id)}/content`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Asset-Media-Type': input.mediaType,
        'X-Content-SHA256': input.sha256,
      },
      body: input.file,
    }),
  updateAdminAsset: (id: string, altText: string | null) =>
    request<AdminAsset>(`/admin/assets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ altText }),
    }),
  archiveAdminAsset: (id: string) =>
    request<AdminAsset>(`/admin/assets/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  listAdminExperiences: (
    input: {
      editionId?: string
      cursor?: string
      limit?: number
    } = {},
  ) =>
    request<AdminExperiencePage>(
      `/admin/experiences${queryString({
        editionId: input.editionId,
        cursor: input.cursor,
        limit: input.limit ?? 100,
      })}`,
    ),
  createAdminExperience: (input: CreateAdminExperience) =>
    request<AdminExperience>('/admin/experiences', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateAdminExperience: (id: string, input: UpdateAdminExperience) =>
    request<AdminExperience>(`/admin/experiences/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archiveAdminExperience: (id: string) =>
    request<AdminExperience>(`/admin/experiences/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  listAdminOffers: () =>
    request<operations['listAdminOffers']['responses'][200]['content']['application/json']>(
      '/admin/offers',
    ),
  createAdminOffer: (input: CreateAdminOffer) =>
    request<AdminOffer>('/admin/offers', { method: 'POST', body: JSON.stringify(input) }),
  updateAdminOffer: (id: string, input: UpdateAdminOffer) =>
    request<AdminOffer>(`/admin/offers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archiveAdminOffer: (id: string) =>
    request<AdminOffer>(`/admin/offers/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  listAdminCoupons: () =>
    request<operations['listAdminCoupons']['responses'][200]['content']['application/json']>(
      '/admin/coupons',
    ),
  createAdminCoupon: (input: CreateAdminCoupon) =>
    request<AdminCoupon>('/admin/coupons', { method: 'POST', body: JSON.stringify(input) }),
  updateAdminCoupon: (id: string, input: UpdateAdminCoupon) =>
    request<AdminCoupon>(`/admin/coupons/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archiveAdminCoupon: (id: string) =>
    request<AdminCoupon>(`/admin/coupons/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getAdminSiteSettings: () => request<AdminSiteSettingsView>('/admin/site-settings'),
  replaceAdminSiteSettingsDraft: (input: SiteSettings) =>
    request<AdminSiteSettingsView>('/admin/site-settings', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  publishAdminSiteSettings: () =>
    request<AdminSiteSettingsView>('/admin/site-settings/publication', { method: 'PUT' }),
  requestNewsletterSubscription: (email: string) =>
    request<{ accepted: true }>('/newsletter/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ email, consent: true, source: 'home' }),
    }),
  confirmNewsletterSubscription: (token: string) =>
    request<{ status: 'active' }>('/newsletter/subscriptions/confirmation', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  cancelNewsletterSubscription: (token: string) =>
    request<{ status: 'unsubscribed' }>('/newsletter/subscriptions/cancellation', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  listAdminNewsletterSubscriptions: (input: {
    search?: string
    status?: 'pending' | 'active' | 'unsubscribed'
    cursor?: string
    limit?: number
  }) =>
    request<AdminNewsletterPage>(
      `/admin/newsletter/subscriptions${queryString({
        search: input.search,
        status: input.status,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  unsubscribeAdminNewsletterSubscription: (id: string) =>
    request<AdminNewsletterSubscription>(
      `/admin/newsletter/subscriptions/${encodeURIComponent(id)}/cancellation`,
      { method: 'POST' },
    ),
  getAdminOperationsSummary: (input: { from?: string; to?: string }) =>
    request<AdminOperationsSummary>(
      `/admin/operations/summary${queryString({ from: input.from, to: input.to })}`,
    ),
  listAdminCustomers: (input: {
    search?: string
    status?: 'active' | 'blocked'
    cursor?: string
    limit?: number
  }) =>
    request<AdminCustomerPage>(
      `/admin/customers${queryString({
        search: input.search,
        status: input.status,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  listAdminOrders: (input: {
    search?: string
    accountId?: string
    status?: 'pending' | 'paid' | 'expired' | 'cancelled' | 'refunded'
    from?: string
    to?: string
    cursor?: string
    limit?: number
  }) =>
    request<AdminOrderPage>(
      `/admin/orders${queryString({
        search: input.search,
        accountId: input.accountId,
        status: input.status,
        from: input.from,
        to: input.to,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  getAdminOrder: (id: string) =>
    request<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(id)}`),
  listAdminPayments: (input: {
    search?: string
    orderId?: string
    status?: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'refunded'
    provider?: string
    from?: string
    to?: string
    cursor?: string
    limit?: number
  }) =>
    request<AdminPaymentPage>(
      `/admin/payments${queryString({
        search: input.search,
        orderId: input.orderId,
        status: input.status,
        provider: input.provider,
        from: input.from,
        to: input.to,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  listAdminEntitlements: (input: {
    search?: string
    accountId?: string
    editionId?: string
    source?: 'order' | 'admin_grant' | 'promotion' | 'legacy_purchase'
    state?: 'active' | 'expired' | 'revoked'
    cursor?: string
    limit?: number
  }) =>
    request<AdminEntitlementPage>(
      `/admin/entitlements${queryString({
        search: input.search,
        accountId: input.accountId,
        editionId: input.editionId,
        source: input.source,
        state: input.state,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  grantAdminEntitlement: (input: GrantAdminEntitlement) =>
    request<{ entitlement: AdminEntitlement; changed: boolean }>('/admin/entitlements', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeAdminEntitlement: (id: string, input: RevokeAdminEntitlement) =>
    request<{ entitlement: AdminEntitlement; changed: boolean }>(
      `/admin/entitlements/${encodeURIComponent(id)}/revocation`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  listAdminAuditLogs: (input: {
    search?: string
    action?: string
    resourceType?: string
    resourceId?: string
    from?: string
    to?: string
    cursor?: string
    limit?: number
  }) =>
    request<AdminAuditLogPage>(
      `/admin/audit-logs${queryString({
        search: input.search,
        action: input.action,
        resourceType: input.resourceType,
        resourceId: input.resourceId,
        from: input.from,
        to: input.to,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  listAdminMigrationRuns: (input: {
    status?: AdminMigrationRunStatus
    cursor?: string
    limit?: number
  }) =>
    request<AdminMigrationRunPage>(
      `/admin/migration-runs${queryString({
        status: input.status,
        cursor: input.cursor,
        limit: input.limit ?? 50,
      })}`,
    ),
  registerAdminMigrationManifest: (manifest: AdminMigrationManifest) =>
    request<{ run: AdminMigrationRunDetail; reused: boolean }>('/admin/migration-runs', {
      method: 'POST',
      body: JSON.stringify({ manifest }),
    }),
  getAdminMigrationRun: (id: string) =>
    request<AdminMigrationRunDetail>(`/admin/migration-runs/${encodeURIComponent(id)}`),
  listAdminMigrationItems: (
    id: string,
    input: {
      kind?: AdminMigrationItemKind
      status?: AdminMigrationItemStatus
      cursor?: string
      limit?: number
    },
  ) =>
    request<AdminMigrationItemPage>(
      `/admin/migration-runs/${encodeURIComponent(id)}/items${queryString({
        kind: input.kind,
        status: input.status,
        cursor: input.cursor,
        limit: input.limit ?? 50,
      })}`,
    ),
  uploadAdminMigrationItemContent: (input: {
    runId: string
    itemId: string
    file: File
    mediaType: string
    sha256: string
  }) =>
    request<{
      item: AdminMigrationItem
      run: AdminMigrationRun
      reused: boolean
    }>(
      `/admin/migration-runs/${encodeURIComponent(input.runId)}/items/${encodeURIComponent(input.itemId)}/content`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Asset-Media-Type': input.mediaType,
          'X-Content-SHA256': input.sha256,
        },
        body: input.file,
      },
    ),
  executeAdminMigrationRun: (id: string, confirmation: string) =>
    request<AdminMigrationRunDetail>(`/admin/migration-runs/${encodeURIComponent(id)}/execution`, {
      method: 'POST',
      body: JSON.stringify({ confirmation, confirmWrites: true }),
    }),
  verifyAdminMigrationRun: (id: string) =>
    request<AdminMigrationRunDetail>(
      `/admin/migration-runs/${encodeURIComponent(id)}/verification`,
      { method: 'POST' },
    ),
  rollbackAdminMigrationRun: (id: string, input: { reason: string; confirmation: string }) =>
    request<AdminMigrationRunDetail>(`/admin/migration-runs/${encodeURIComponent(id)}/rollback`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getAdminSystemHealth: () => request<AdminSystemHealth>('/admin/system-health'),
  getAdminTechnicalReports: (input: { from?: string; to?: string }) =>
    request<AdminTechnicalReports>(
      `/admin/technical-reports${queryString({ from: input.from, to: input.to })}`,
    ),
  listAdminOutboxEvents: (input: {
    status?: AdminOutboxEventStatus
    topic?: string
    from?: string
    to?: string
    cursor?: string
    limit?: number
  }) =>
    request<AdminOutboxEventPage>(
      `/admin/outbox-events${queryString({
        status: input.status,
        topic: input.topic,
        from: input.from,
        to: input.to,
        cursor: input.cursor,
        limit: input.limit ?? 20,
      })}`,
    ),
  createAdminOutboxRedelivery: (id: string, input: CreateAdminOutboxRedelivery) =>
    request<AdminOutboxRedelivery>(`/admin/outbox-events/${encodeURIComponent(id)}/redeliveries`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  createAdminPaymentReconciliation: (id: string, input: CreateAdminPaymentReconciliation) =>
    request<AdminPaymentReconciliation>(
      `/admin/payments/${encodeURIComponent(id)}/reconciliations`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
}
